import { useState, useCallback, useEffect, type ReactNode } from 'react';
import { HoverCard, Stack, Text, Button } from '@cowbell/ui/core';
import { useMooConfig } from '../../provider';
import type { Source } from '../../types/sources';

function getActionLabel(type: string): string {
  const labels: Record<string, string> = {
    quote: 'View quote',
    policy: 'View policy',
    claim: 'View claim',
    submission: 'View submission',
    binder: 'View binder',
    uw_entry: 'Open UW Review',
    account: 'View account',
    agency: 'View agency',
    pdf: 'Open preview',
    spreadsheet: 'Open preview',
    document: 'Open preview',
    image: 'Open preview',
    jira: 'Open in Jira',
    slack: 'Open in Slack',
    confluence: 'Open in Confluence',
    email: 'Open email',
    diary_entry: 'View diary entry',
    comparables: 'View comparables',
    threat_actor: 'View threat actor',
    findings: 'View findings',
    url: 'Open link',
    cve: 'View CVE',
  };
  return labels[type] ?? 'Open preview';
}

export interface SourceTooltipProps {
  readonly source: Source;
  readonly children: ReactNode;
}

export function SourceTooltip({ source, children }: SourceTooltipProps) {
  const { getSourceUrl, onSourceClick } = useMooConfig();
  const [copied, setCopied] = useState(false);
  const sourceUrl = getSourceUrl?.(source) ?? source.url ?? '';

  const handleActionClick = useCallback(() => {
    onSourceClick?.(source);
  }, [source, onSourceClick]);

  const handleCopyLink = useCallback(() => {
    if (!sourceUrl) return;
    navigator.clipboard
      .writeText(sourceUrl)
      .then(() => {
        setCopied(true);
      })
      .catch(() => undefined);
  }, [sourceUrl]);

  useEffect(() => {
    if (!copied) return undefined;
    const id = globalThis.setTimeout(() => setCopied(false), 2000);
    return () => globalThis.clearTimeout(id);
  }, [copied]);

  const label = getActionLabel(source.type);

  return (
    <HoverCard
      openDelay={300}
      closeDelay={200}
      withArrow
      zIndex={1300}
      floatingStrategy="fixed"
    >
      <HoverCard.Target>{children}</HoverCard.Target>
      <HoverCard.Dropdown>
        <Stack gap="xs" p="xs" style={{ minWidth: 200, maxWidth: 280 }}>
          <Text size="sm" fw={600} lineClamp={2}>
            {source.title}
          </Text>
          {source.type === 'url' && (
            <Text size="xs" c="teal" data-testid="web-source-domain">
              Web result ·{' '}
              {(source.metadata as { domain?: string })?.domain || sourceUrl}
            </Text>
          )}
          {source.description && (
            <Text size="xs" c="dimmed" lineClamp={3}>
              {source.description}
            </Text>
          )}
          <Button
            size="xs"
            variant="light"
            onClick={handleActionClick}
            aria-label={label}
          >
            {label}
          </Button>
          {sourceUrl && (
            <Button
              size="xs"
              variant="subtle"
              color="gray"
              onClick={handleCopyLink}
              aria-label={copied ? 'Copied!' : 'Copy link'}
            >
              {copied ? 'Copied!' : 'Copy link'}
            </Button>
          )}
        </Stack>
      </HoverCard.Dropdown>
    </HoverCard>
  );
}
